import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styles from "./Bitiruvchilar.module.css";
import activityStyles from "./activityCards.module.css";
import classes from "./Activities.module.css";
import { testUrl } from "../BaseData";
import Pagination from "@mui/material/Pagination";

const ALUMNI_PER_PAGE = 3;

// Small line icons (inherit color via currentColor). Decorative -> aria-hidden.
const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
};
const IconLocation = () => (
    <svg {...iconProps}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);
const IconEducation = () => (
    <svg {...iconProps}>
        <path d="M22 10 12 5 2 10l10 5 10-5z" />
        <path d="M6 12v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
    </svg>
);
const IconWork = () => (
    <svg {...iconProps}>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
);
const IconSearch = () => (
    <svg {...iconProps}>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
    </svg>
);
const IconUser = () => (
    <svg {...iconProps}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

// Pie chart palette (brand navy family + gold + accents) and geometry helpers
const PIE_COLORS = ['#133654', '#1d4e78', '#2f6ea6', '#c9a227', '#3f8f8a', '#6a4e9c', '#b9741f', '#5b8bc0', '#8a90a5'];

const polarXY = (cx, cy, r, frac) => {
    const ang = 2 * Math.PI * frac - Math.PI / 2; // 0 = top, clockwise
    return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
};

const arcPath = (cx, cy, r, start, end) => {
    const [x1, y1] = polarXY(cx, cy, r, start);
    const [x2, y2] = polarXY(cx, cy, r, end);
    const large = end - start > 0.5 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
};

const Bitiruvchilar = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Activities state
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [pageApi, setPageApi] = useState(1);
    const activityItemsPerPage = 9;

    // Alumni state
    const [alumniLoading, setAlumniLoading] = useState(true);
    const [alumni, setAlumni] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [alumniPage, setAlumniPage] = useState(1);

    // Fetch activities
    useEffect(() => {
        setActivitiesLoading(true);
        axios.get(testUrl + "/api/activitiestype/")
            .then((res) => {
                const types = res.data;
                const targetType = types.find(type =>
                    (type.name_uz && type.name_uz.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_ru && type.name_ru.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_en && type.name_en.toLowerCase().includes("bitiruvchi")) ||
                    type.name_uz === "Bizning bitiruvchilar"
                );
                const shortName = targetType && targetType.short_name ? targetType.short_name : "bizning-bitiruvchilar";
                return axios.get(testUrl + `/api/activities/${shortName}/`);
            })
            .then((response) => {
                if (response && response.data) {
                    setActivities(response.data);
                }
            })
            .catch((err) => console.error("Error fetching activities:", err))
            .finally(() => setActivitiesLoading(false));
    }, [i18n.language]);

    // Fetch alumni
    useEffect(() => {
        setAlumniLoading(true);
        axios.get(testUrl + "/api/alumni/")
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setAlumni(data);
            })
            .catch((err) => console.error("Error fetching alumni:", err))
            .finally(() => setAlumniLoading(false));
    }, [i18n.language]);

    // Filter alumni
    const filteredAlumni = useMemo(() => {
        let result = alumni;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(a =>
                (a.full_name && a.full_name.toLowerCase().includes(q)) ||
                (a.name_uz && a.name_uz.toLowerCase().includes(q)) ||
                (a.surname_uz && a.surname_uz.toLowerCase().includes(q)) ||
                (a.faculty && a.faculty.toLowerCase().includes(q)) ||
                (a.education_field && a.education_field.toLowerCase().includes(q)) ||
                (a.workplace && a.workplace.toLowerCase().includes(q)) ||
                (a.address && a.address.toLowerCase().includes(q))
            );
        }
        return result;
    }, [alumni, searchQuery]);

    useEffect(() => { setAlumniPage(1); }, [searchQuery]);

    // Ta'lim yo'nalishi kesimida bitiruvchilar soni. Avval "Ta'lim yo'nalishi"
    // (education_field) olinadi; u bo'sh bo'lsa "Fakultet"ga tushib qolamiz.
    const fieldStats = useMemo(() => {
        const tally = (getter) => {
            const map = new Map();
            alumni.forEach((a) => {
                const raw = getter(a);
                const label = raw && String(raw).trim();
                if (!label) return;
                // normalize key (case + inner spaces) so "X" and "x  x" don't split
                const key = label.replace(/\s+/g, ' ').toLowerCase();
                const existing = map.get(key);
                if (existing) existing.count += 1;
                else map.set(key, { field: label, count: 1 });
            });
            return map;
        };
        let map = tally((a) => a.education_field);
        if (map.size === 0) map = tally((a) => a.faculty);
        return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }, [alumni]);

    // Pie slices (cumulative fractions + percentage + brand color)
    const pieSlices = useMemo(() => {
        const total = fieldStats.reduce((s, f) => s + f.count, 0);
        if (!total) return [];
        const n = fieldStats.length;
        let acc = 0;
        return fieldStats.map((f, i) => {
            const start = acc / total;
            acc += f.count;
            let color = PIE_COLORS[i % PIE_COLORS.length];
            // first & last slices touch at 12 o'clock — avoid identical colors there
            if (i === n - 1 && i !== 0 && color === PIE_COLORS[0]) {
                color = PIE_COLORS[(i + 1) % PIE_COLORS.length];
            }
            return {
                ...f,
                start,
                end: acc / total,
                pct: (f.count / total) * 100,
                color,
            };
        });
    }, [fieldStats]);

    const totalAlumniPages = Math.ceil(filteredAlumni.length / ALUMNI_PER_PAGE);
    const paginatedAlumni = filteredAlumni.slice(
        (alumniPage - 1) * ALUMNI_PER_PAGE,
        alumniPage * ALUMNI_PER_PAGE
    );

    // Activities pagination
    const currentActivities = activities.slice(
        (pageApi - 1) * activityItemsPerPage,
        pageApi * activityItemsPerPage
    );

    const getDisplayName = (a) => {
        if (a.full_name) return a.full_name;
        return `${a.surname_uz || ''} ${a.name_uz || ''}`.trim() || "Noma'lum";
    };

    // First 1-2 letters of the name for the no-photo monogram (Latin/Cyrillic)
    const getInitials = (a) => {
        const name = (a.full_name || `${a.surname_uz || ''} ${a.name_uz || ''}`).trim();
        const letters = name.split(/\s+/)
            .map((p) => p[0])
            .filter((ch) => ch && /\p{L}/u.test(ch));
        return letters.slice(0, 2).join('').toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        // birth_date is a "YYYY-MM-DD" string; read the year directly to avoid
        // new Date() UTC/local timezone shifts on Jan-1 dates.
        const match = String(dateStr).match(/(\d{4})/);
        return match ? match[1] : dateStr;
    };

    return (
        <div className={styles.pageWrapper}>
            {/* ===== BITIRUVCHILARIMIZ SECTION ===== */}
            <section className={styles.alumniSection}>
                {/* Section header */}
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderLeft}>
                        <h1 className={styles.sectionTitle}>BITIRUVCHILARIMIZ</h1>
                    </div>
                    <div className={styles.sectionHeaderRight}>
                        <div className={styles.searchWrapper}>
                            <span className={styles.searchIcon}><IconSearch /></span>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={t("Qidirish...")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Alumni cards grid */}
                {alumniLoading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        {t("Yuklanmoqda...")}
                    </div>
                ) : paginatedAlumni.length > 0 ? (
                    <>
                        <div className={styles.alumniGrid}>
                            {paginatedAlumni.map((item) => (
                                <div key={item.id} className={styles.alumniCard}>
                                    <div className={styles.cardBody}>
                                        {/* Photo + name pill */}
                                        <div className={styles.cardPhotoSection}>
                                            <div className={styles.cardPhotoFrame}>
                                                {item.image ? (
                                                    <img src={item.image} alt={getDisplayName(item)} className={styles.cardPhoto} />
                                                ) : (
                                                    <div className={styles.cardPhotoPlaceholder} aria-hidden="true">
                                                        {getInitials(item) || <IconUser />}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Info */}
                                        <div className={styles.cardInfoSection}>
                                            <p className={styles.infoName}>{getDisplayName(item)}</p>

                                            {(item.address || item.birth_date) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconLocation /></span>
                                                    <p className={styles.infoText}>
                                                        {item.address && <>{item.address}{item.birth_date ? 'da' : '.'}</>}
                                                        {item.address && item.birth_date && <br />}
                                                        {item.birth_date && <>{formatDate(item.birth_date)} yilda tug'ilgan.</>}
                                                    </p>
                                                </div>
                                            )}

                                            {(item.graduation_year || item.education_field) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconEducation /></span>
                                                    <p className={styles.infoText}>
                                                        {item.graduation_year && `${item.graduation_year} yil`}
                                                        {item.education_field ? (
                                                            <>
                                                                {item.graduation_year && <br />}
                                                                <span className={styles.infoHighlight}>{item.education_field}</span>
                                                                <br />
                                                                ta'lim yo'nalishini tamomlagan.
                                                            </>
                                                        ) : (
                                                            <> ta'lim yo'nalishini tamomlagan.</>
                                                        )}
                                                    </p>
                                                </div>
                                            )}

                                            {(item.workplace || item.position) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconWork /></span>
                                                    <p className={styles.infoText}>
                                                        {item.workplace && <>Hozirgi vaqtda {item.workplace}{item.position ? 'da' : ''}</>}
                                                        {item.position && (
                                                            <>{item.workplace ? <br /> : 'Hozirgi vaqtda '}{item.position} lavozimida ishlab kelmoqda.</>
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {totalAlumniPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
                                <Pagination
                                    onChange={(e, value) => setAlumniPage(value)}
                                    count={totalAlumniPages}
                                    page={alumniPage}
                                    variant="outlined"
                                    shape="rounded"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}><IconEducation /></div>
                        <p className={styles.emptyStateText}>Bitiruvchilar topilmadi</p>
                        <p className={styles.emptyStateSubText}>
                            Ma'lumotlar admin paneldan Excel orqali yuklanadi
                        </p>
                    </div>
                )}

                {/* ===== Yo'nalishlar kesimida pie chart ===== */}
                {!alumniLoading && alumni.length > 0 && (
                    <div className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>Yo'nalishlar kesimida bitiruvchilar</h2>
                        {fieldStats.length > 0 ? (
                            <>
                            <p className={styles.chartCaption}>Barcha bitiruvchilar bo'yicha</p>
                            <div className={styles.pieWrap}>
                                <svg className={styles.pieSvg} viewBox="0 0 200 200" role="img"
                                    aria-label="Yo'nalishlar bo'yicha diagramma">
                                    {pieSlices.length === 1 ? (
                                        <circle cx="100" cy="100" r="92" fill={pieSlices[0].color}
                                            stroke="#ffffff" strokeWidth="2" />
                                    ) : (
                                        pieSlices.map((s) => (
                                            <path key={s.field} d={arcPath(100, 100, 92, s.start, s.end)}
                                                fill={s.color} stroke="#ffffff" strokeWidth="2" />
                                        ))
                                    )}
                                </svg>
                            </div>
                            <ul className={styles.pieLegend}>
                                {pieSlices.map((s) => (
                                    <li key={s.field} className={styles.pieLegendItem}>
                                        <span className={styles.pieDot} style={{ background: s.color }}></span>
                                        <span className={styles.pieLegendLabel} title={s.field}>{s.field}</span>
                                        <span className={styles.pieLegendValue}>{s.count} ({s.pct.toFixed(1)}%)</span>
                                    </li>
                                ))}
                            </ul>
                            </>
                        ) : (
                            <p className={styles.chartEmpty}>Yo'nalish bo'yicha ma'lumot kiritilmagan.</p>
                        )}
                    </div>
                )}
            </section>

            {/* ===== FAOLIYAT SECTION ===== */}
            <section className={styles.activitiesSection}>
                <div data-aos="fade-up" className={classes["container"]}>
                    <h1 className={classes["page-title"]}>{t("Faoliyat")}</h1>
                    {activitiesLoading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            {t("Yuklanmoqda...")}
                        </div>
                    ) : (
                        <>
                            <div className={activityStyles.facultyGrid}>
                                {currentActivities.filter(Boolean).map((item) => (
                                    <div key={item.id} className={activityStyles.activityCard}
                                        onClick={() => navigate(`/activity/${item.id}`)}>
                                        <img src={item.image} alt=" " className={activityStyles.activityImage} />
                                        <div className={activityStyles.activityInfo}>
                                            <h3>{item[`name_${i18n.language}`] || item.name_uz}</h3>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {activities.length > activityItemsPerPage && (
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                                    <Pagination
                                        onChange={(e, value) => setPageApi(value)}
                                        count={Math.ceil(activities.length / activityItemsPerPage)}
                                        page={pageApi}
                                        variant="outlined"
                                        shape="rounded"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Bitiruvchilar;
